import React, { useId, useMemo } from "react";

const SIZES = { sm: 14, md: 18, lg: 22 };

/** Small deterministic generator, so each pin keeps its own quirks between renders. */
const seeded = (text) => {
  let state = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    state = Math.imul(state ^ text.charCodeAt(i), 16777619);
  }
  return () => {
    state = Math.imul(state ^ (state >>> 15), 2246822507);
    state = Math.imul(state ^ (state >>> 13), 3266489909);
    state ^= state >>> 16;
    return (state >>> 0) / 4294967296;
  };
};

/**
 * Nobody pushes a pin in dead centre or perfectly straight. Each one sits a little off to
 * one side and leans its own way: `lean` is how far the barrel runs from the head back to
 * the board, per step, and `squash` foreshortens the head along that same direction.
 */
const pinQuirks = (seed) => {
  const next = seeded(seed);
  const angle = next() * Math.PI * 2;
  const tilt = 0.35 + next() * 1.35;
  return {
    "--pin-x": `${Math.round((next() * 2 - 1) * 20)}px`,
    "--pin-y": `${(next() * 5 - 1.5).toFixed(1)}px`,
    "--pin-lx": (Math.cos(angle) * tilt).toFixed(2),
    "--pin-ly": (Math.sin(angle) * tilt).toFixed(2),
    "--pin-angle": `${Math.round((angle * 180) / Math.PI)}deg`,
    "--pin-squash": (1 - tilt * 0.13).toFixed(3),
    "--pin-spin": `${Math.round(next() * 360)}deg`,
  };
};

export const EvidencePin = ({ color = "red", size = "md", className = "" }) => {
  const id = useId();
  const px = SIZES[size] || size;
  const quirks = useMemo(() => pinQuirks(id), [id]);

  return (
    <span
      className={`evidence-pin pin-${color} ${className}`}
      style={{ width: px, height: px, ...quirks }}
      aria-hidden="true"
    />
  );
};

export default EvidencePin;
